import React, { useCallback, useEffect, useRef, useState } from "react";
import { MentionsInput, Mention, MentionSelection } from "react-mentions-ts";
import { api } from "../api";

type MentionData = {
  id: string | number;
  display: string;
  avatar_url?: string;
};

export type MentionTextareaProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

export const MentionTextarea = ({
  value,
  onChange,
  placeholder,
}: MentionTextareaProps): JSX.Element => {
  const [isDark, setIsDark] = useState(false);
  const [selectedMention, setSelectedMention] =
    useState<MentionSelection | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setIsDark(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const [suggestions, setSuggestions] = useState<MentionData[]>([]);
  const queryRef = useRef("");

  // Sync fetch whenever the value changes and contains an active @ query
  useEffect(() => {
    const match = value.match(/@(\w*)$/);
    const query = match ? match[1] : "";

    if (query === queryRef.current) return;
    queryRef.current = query;

    if (!query) {
      setSuggestions([]);
      return;
    }

    api
      .get<"/api/search/:search">(
        `/api/search/${encodeURIComponent(query)}?types=users`
      )
      .then((result) => {
        if (result && !api.isToast(result)) {
          setSuggestions(
            result.users.map((u) => ({
              id: u.username,
              display: u.username,
              avatar_url: u.avatar_url,
            }))
          );
        }
      });
  }, [value]);

  const handleMentionSelectionChange = useCallback(
    (selections: MentionSelection[]) => {
      if (selections.length > 0) {
        setSelectedMention(selections[0]);
      } else {
        setSelectedMention(null);
      }
    },
    []
  );

  const controlClass = `
    min-h-[150px] w-full rounded border text-[15px] leading-[1.6] font-light
    ${isDark ? "border-slate-600 bg-slate-900" : "border-slate-200 bg-white"}
  `;

  const inputClass = `outline-none bg-transparent p-3 min-h-[150px] overflow-y-auto ${isDark ? "text-slate-100" : "text-slate-900"}`;

  // The suggestions wrapper - override library's border-border class
  const suggestionsClass = isDark
    ? "[--border:theme(colors.slate.600)] border-slate-600 rounded-lg overflow-hidden"
    : "[--border:theme(colors.slate.200)] rounded-lg overflow-hidden";

  const suggestionsListClass = `
    rounded-lg shadow-lg overflow-hidden max-h-[280px]
    ${isDark ? "bg-slate-800" : "bg-white"}
  `;

  const suggestionItemClass = `
    ${isDark ? "border-b border-slate-700 last:border-b-0" : "border-b border-slate-100 last:border-b-0"}
  `;

  const suggestionItemFocusedClass = isDark ? "bg-slate-700" : "bg-slate-100";

  return (
    <div ref={containerRef} className="relative">
      <MentionsInput
        value={value}
        onMentionsChange={({ value: newValue }) => onChange(newValue)}
        onMentionSelectionChange={handleMentionSelectionChange}
        placeholder={placeholder}
        a11ySuggestionsListLabel="Suggested users"
        classNames={{
          control: controlClass,
          input: inputClass,
          highlighter: "p-3",
          suggestions: suggestionsClass,
          suggestionsList: suggestionsListClass,
          suggestionItem: suggestionItemClass,
          suggestionItemFocused: suggestionItemFocusedClass,
        }}
      >
        <Mention
          trigger="@"
          data={suggestions}
          markup="@[__display__]"
          displayTransform={(_id, display) => `@${display}`}
          appendSpaceOnAdd
          className={`font-semibold rounded px-1 ${isDark ? "bg-blue-500/25 text-blue-300" : "bg-blue-100 text-blue-700"}`}
          renderSuggestion={(entry, search, _highlighted, _index, focused) => {
            const display = String(entry.display || entry.id);
            const matchIndex = display.toLowerCase().indexOf(search.toLowerCase());

            let highlighted: React.ReactNode = display;
            if (matchIndex >= 0) {
              const before = display.slice(0, matchIndex);
              const match = display.slice(matchIndex, matchIndex + search.length);
              const after = display.slice(matchIndex + search.length);
              highlighted = (
                <>
                  {before}
                  <span className="text-amber-400">{match}</span>
                  {after}
                </>
              );
            }

            return (
              <div
                className={`
                  flex items-center gap-3 px-3 py-2
                  ${focused ? suggestionItemFocusedClass : ""}
                `}
              >
                <img
                  src={
                    (entry as MentionData).avatar_url || "/img/default-chip.png"
                  }
                  className="h-8 w-8 rounded-full object-cover"
                  alt=""
                />
                <div
                  className={`font-normal text-[15px] ${isDark ? "text-white" : "text-slate-900"}`}
                >
                  {highlighted}
                </div>
              </div>
            );
          }}
        />
      </MentionsInput>

      {selectedMention && (
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            window.open(`/user/${selectedMention.id}`, "_blank");
          }}
          className={`
            absolute z-50 px-3 py-1.5 rounded-md text-sm font-normal
            shadow-lg transition-opacity cursor-pointer
            ${
              isDark
                ? "bg-slate-700 text-white hover:bg-slate-600"
                : "bg-slate-800 text-white hover:bg-slate-700"
            }
          `}
          style={{
            bottom: "100%",
            left: "12px",
            marginBottom: "8px",
          }}
        >
          Go to {selectedMention.display}'s profile ↗
        </button>
      )}
    </div>
  );
};
